<template>
  <v-container fluid pt-0>
    <v-layout align-center justify-end>
      <v-flex xs12 sm3>
        <v-layout>
          <v-select
            v-if="showGroupView"
            :items="groupOptions"
            v-model="selectedGroupOption"
            label="Group by"
            item-text="label"
            return-object
            :outlined="outlinedInputs"
          ></v-select>
        </v-layout>
      </v-flex>
      <v-switch
        prepend-icon="mdi-view-list"
        class="mr-3"
        label="Group View"
        v-model="showGroupView"
      ></v-switch>
      <div>
        <list-visible-columns-and-filter
          :visible-columns="visibleColumns"
          :selectable-headers="extrasHeaders"
          v-model="search"
          @change="onVisibleColumnsChange"
        ></list-visible-columns-and-filter>
      </div>
      <v-dialog v-model="supplierAddExtrasDialog" persistent scrollable>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            color="primary"
            :disabled="$store.getters.hasAccessTo('suppliers:view_only')"
          >
            <v-icon left>mdi-plus-circle</v-icon>Add Extras
          </v-btn>
        </template>
        <supplier-add-extras
          v-if="supplierAddExtrasDialog"
          :supplier="supplier"
          @onSave="closeAndRefetch"
          @onClose="onCloseAddExtras"
        ></supplier-add-extras>
      </v-dialog>
    </v-layout>
    <v-row>
      <v-col class="px-0 pt-0">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="4">
                <dates-range-picker
                  :gt-date-from-string="dateFrom"
                  :gt-date-to-string="dateTo"
                  :date-from-range-start="dateFromRangeStart"
                  :date-to-range-end="dateToRangeEnd"
                  @input="onDatesRangeSelected"
                  :outlined="outlinedInputs"
                  :clearable="true"
                  @onClear="onClearDates"
                ></dates-range-picker>
              </v-col>
              <v-col>
                <gt-multi-select
                  :items="services"
                  v-model="selectedServices"
                  item-text="name"
                  item-value="id"
                  label="Service"
                  :outlined="outlinedInputs"
                ></gt-multi-select>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="extraNameFilter"
                  label="Extra Name"
                  :outlined="outlinedInputs"
                  hide-details
                  clearable
                />
              </v-col>
              <v-col>
                <v-switch
                  v-model="serviceNotInUseFilter"
                  label="Not In Use"
                  dense
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-flex v-if="showGroupView">
      <v-flex v-for="key in Object.keys(groupedExtras)" :key="key">
        <h5 class="headline text-xs-left pb-3 pt-3">
          {{ groupedExtras[key].label }}
        </h5>
        <component
          :is="selectedGroupOption.component"
          :group="groupedExtras[key]"
          :headers="extrasHeaders"
          :visible-columns="visibleColumns"
          @onEditItem="onEditItem"
          @onDeleteItem="onDeleteItem"
          :hide-delete="
            $store.getters.hasAccessTo('suppliers:view_only') ||
            !$store.getters.hasAccessTo('suppliers:delete')
          "
        ></component>
      </v-flex>
    </v-flex>
    <v-flex v-show="!showGroupView">
      <supplier-extras-list
        :extras="extras"
        :search="search"
        :extras-headers="extrasHeaders"
        :visible-columns="visibleColumns"
        @onEditItem="onEditItem"
        @onDeleteItem="onDeleteItem"
        @onCloneItem="onCloneItem"
        @onCloneItems="onCloneItems"
        @onUpdateItem="onUpdateItem"
        @onBatchUpdate="onBatchUpdate"
        @onDeleteRecords="onDeleteRecords"
        ref="extrasList"
      ></supplier-extras-list>
    </v-flex>
    <v-dialog v-model="supplierExtraEditorDialog" persistent width="1100">
      <supplier-extra-editor
        :extra="editedItem"
        @onClose="supplierExtraEditorDialog = false"
        @onSave="closeAndRefetch"
      ></supplier-extra-editor>
    </v-dialog>
    <clone-extra
      :show="showCloneExtra"
      :services="services"
      v-model="selectedExtra"
      @close="showCloneExtra = false"
      @complete="onCloneComplete"
      :extras="selectedExtras"
    ></clone-extra>
    <batch-update-dialog
      :show="showBatchUpdateDialog"
      :columns="editableColumns"
      @close="showBatchUpdateDialog = false"
      @update="onUpdateRecords"
    />
    <v-row v-if="debug">
      <v-col> {{ serviceNotInUseFilter }}--{{ services }} </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SupplierAddExtras from "./SupplierAddExtras";
import SimpleDataTableGroup from "./SimpleDataTableGroup";
import SupplierExtraEditor from "./SupplierExtraEditor";
import {
  EXTRA_DELETE_MUTATION,
  EXTRA_UPDATE_MUTATION,
  UPDATE_RECORDS_MUTATION,
} from "../../../../constants/graphql";
import gql from "graphql-tag";
import SupplierExtrasList from "./SupplierExtrasList";
import { sortByDate } from "../../../../helpers/index";
import CloneExtra from "./CloneExtra.vue";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter.vue";
import DatesRangePicker from "../../shared/DatesRangePicker.vue";
import GtMultiSelect from "../../shared/GtMultiSelect.vue";
import BatchUpdateDialog from "../../shared/BatchUpdateDialog.vue";
const moment = require("moment");
export const QUERY = gql`
  query supplierExtras(
    $supplierId: ID!
    $extraValidTo_gte: DateTimeType
    $dateFrom: DateTimeType
    $dateTo: DateTimeType
    $extraServices: [ID]
    $extraName_contains: String
    $serviceNotInUse: Boolean
  ) {
    supplier(id: $supplierId) {
      id
      currencyCode
      name
      serviceType
      services(orderBy: { fieldName: "name", direction: ASC }) {
        totalCount
        edges {
          node {
            id
            name
            notInUse #https://tracker.globetrack.ie/issues/6917
          }
        }
      }
      extras(
        filter: {
          validTo_gte: $extraValidTo_gte
          dateFrom: $dateFrom
          dateTo: $dateTo
          services: $extraServices
          name_contains: $extraName_contains
          serviceNotInUse: $serviceNotInUse
        }
      ) {
        edges {
          node {
            id
            agentGroupName
            commission
            cost
            currencyCode
            discount
            groupId
            markup
            name
            percentPrice
            rateTypeCode
            sell
            isChildOnly
            isInfantOnly
            isMandatory
            capacityChange
            marketplace
            noReport
            noVoucher
            service {
              id
              name
              code
              typeName
              minOccupancy
              maxOccupancy
              stdOccupancy
            }
            typeId
            validFrom
            validTo
            taxCode
          }
        }
      }
    }
  }
`;
export default {
  name: "SupplierExtras",
  components: {
    SupplierExtrasList,
    SupplierAddExtras,
    SimpleDataTableGroup,
    SupplierExtraEditor,
    CloneExtra,
    ListVisibleColumnsAndFilter,
    DatesRangePicker,
    GtMultiSelect,
    BatchUpdateDialog,
  },
  props: {
    supplierId: {
      type: String,
      default: "",
    },
    showTitle: {
      type: Boolean,
      default: () => true,
    },
  },
  data() {
    return {
      debug: false,
      search: "",
      showGroupView: false,
      dateFrom: moment().toISOString(true),
      dateTo: null,
      selectedServices: [],
      selectedExtras: [],
      extraNameFilter: "",
      showBatchUpdateDialog: false,
      groupOptions: [
        {
          label: "Service",
          method: this.getGroupingMethod("serviceName"),
          component: "SimpleDataTableGroup",
        },
        {
          label: "Extra",
          method: this.getGroupingMethod("name"),
          component: "SimpleDataTableGroup",
        },
      ],
      selectedGroupOption: {
        label: "Service",
        method: this.getGroupingMethod("serviceName"),
        component: "SimpleDataTableGroup",
      },
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "validFrom",
        "validTo",
        "serviceName",
        "extraName",
        "rateTypeCode",
        "cost",
        "sell",
        "childOnly",
        "actions",
      ]),

      supplierAddedExtras: [],
      supplier: {},
      supplierAddExtrasDialog: false,
      supplierExtraEditorDialog: false,
      showCloneExtra: false,
      selectedExtra: {},
      editedItem: {},
      serviceNotInUseFilter: false,
      editableColumns: [
        {
          label: "Commission",
          type: "BOOLEAN",
          name: "commission",
        },
        {
          label: "Markup",
          type: "BOOLEAN",
          name: "markup",
        },
        {
          label: "Discount",
          type: "BOOLEAN",
          name: "discount",
        },
        {
          label: "Mandatory",
          type: "BOOLEAN",
          name: "is_mandatory",
        },
        {
          label: "No Report",
          type: "BOOLEAN",
          name: "noReport",
        },
        {
          label: "No Voucher",
          type: "BOOLEAN",
          name: "noVoucher",
        },
        {
          label: "Capacity Change",
          type: "BOOLEAN",
          name: "capacity_change",
        },
        {
          label: "Marketplace",
          type: "BOOLEAN",
          name: "marketplace",
        },
        { label: "Child Only", type: "BOOLEAN", name: "is_child_only" },
        { label: "Infant Only", type: "BOOLEAN", name: "is_infant_only" },
      ],
    };
  },

  computed: {
    extrasHeaders() {
      return [
        {
          text: "Valid From",
          value: "validFrom",
          name: "validFrom",
          type: "date",
          sort: sortByDate,
        },
        {
          text: "Valid To",
          value: "validTo",
          name: "validTo",
          type: "date",
          sort: sortByDate,
        },
        {
          text: "Agent Group",
          value: "agentGroupName",
          name: "agentGroupName",
          type: "text",
        },
        {
          text: "Service Name",
          value: "serviceName",
          name: "serviceName",
          type: "text",
        },
        {
          text: "Extra Name",
          value: "name",
          name: "extraName",
          type: "text",
        },
        {
          text: "Rate Type",
          value: "rateTypeCode",
          name: "rateTypeCode",
          type: "text",
        },
        {
          text: "Tax Code",
          value: "taxCode",
          name: "taxCode",
          type: "text",
        },
        {
          text: "Commission",
          value: "commission",
          name: "commission",
          type: "checkbox",
          align: "center",
        },
        {
          text: "Markup",
          value: "markup",
          name: "markup",
          type: "checkbox",
        },
        {
          text: "Discount",
          value: "discount",
          name: "discount",
          type: "checkbox",
        },
        {
          text: "Mandatory",
          value: "isMandatory",
          name: "isMandatory",
          type: "checkbox",
        },
        {
          text: "No Report",
          value: "noReport",
          name: "noReport",
          type: "checkbox",
        },
        {
          text: "No Voucher",
          value: "noVoucher",
          name: "noVoucher",
          type: "checkbox",
        },
        {
          text: "Capacity Change",
          value: "capacityChange",
          name: "capacityChange",
          type: "checkbox",
        },
        {
          text: "Marketplace",
          value: "marketplace",
          name: "marketplace",
          type: "checkbox",
        },
        {
          text: "Currency",
          value: "currencyCode",
          name: "currencyCode",
          type: "text",
        },
        {
          text: "% of price",
          value: "percentPrice",
          name: "percentPrice",
          type: "text",
        },
        {
          text: "Cost",
          value: "cost",
          name: "cost",
          type: "text",
        },
        {
          text: "Sell",
          value: "sell",
          name: "sell",
          type: "text",
        },
        {
          text: "Child Only",
          value: "isChildOnly",
          name: "childOnly",
          type: "checkbox",
          align: "center",
        },
        {
          text: "Infant Only",
          value: "isInfantOnly",
          name: "infantOnly",
          type: "checkbox",
          align: "center",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          type: "actions",
          align: "center",
        },
      ].filter((h) => {
        if (h.name == "marketplace") {
          return this.$store.getters.hasAccessTo("third_party:marketplace");
        } else {
          return true;
        }
      });
    },
    dateFromRangeStart() {
      return moment().subtract(10, "years").toISOString(true);
    },
    dateToRangeEnd() {
      return moment().add(10, "years").toISOString(true);
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    groupedExtras() {
      const filteredItems =
        this.search === ""
          ? this.extras
          : this.$refs.extrasList.$refs.extrasDataTable.filteredItems;
      return this.selectedGroupOption.method(filteredItems);
    },
    extras() {
      return this.supplier && this.supplier.extras
        ? [...this.supplier.extras.edges.map(this.extraMapper)]
        : [];
    },
    services() {
      return this.supplier && this.supplier.services
        ? this.supplier.services.edges
            .map((s) => {
              return { ...s.node };
            })
            .filter((s) => s.notInUse == this.serviceNotInUseFilter)
        : [];
    },
  },
  methods: {
    onBatchUpdate(items) {
      this.showBatchUpdateDialog = true;
    },
    onUpdateRecords(payload) {
      const records = this.$refs.extrasList.selected.map((item) => item.id);
      const attributes = payload.map((field) => {
        return {
          name: field.name,
          value: field.value.toString(),
        };
      });
      this.$apollo
        .mutate({
          mutation: UPDATE_RECORDS_MUTATION,
          variables: {
            input: {
              records,
              attributes,
              model: "Extra",
            },
          },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Records Updated.",
          });
          this.showBatchUpdateDialog = false;
          this.refetchSupplier();
        });
    },
    onDeleteRecords() {
      this.$refs.extrasList.selected.forEach((item) => {
        this.onDeleteItem(item);
      });
    },
    onDatesRangeSelected(d) {
      //console.log("onDatesRangeSelected", d);
      this.dateFrom = d.dateFrom;
      this.dateTo = d.dateTo;
    },
    onClearDates() {
      //console.log("onClearDates");
      this.dateFrom = null;
      this.dateTo = null;
    },
    onVisibleColumnsChange(event) {
      this.visibleColumns = [...event];
      this.$store.dispatch("updateLocalSettings", {
        [this.$options.name]: event,
      });
    },
    onCloneItem(item) {
      this.selectedExtra = { ...item };
      this.selectedExtras = [];
      this.showCloneExtra = true;
    },
    onCloneItems(items) {
      this.selectedExtra = items[0];
      this.selectedExtras = [...items];
      this.showCloneExtra = true;
    },
    onCloneComplete(payload) {
      this.$store.dispatch("showAppSnackbar", {
        msg: `${payload.name} Created.`,
      });
      this.$emit("onChange", "Extra Saved");
      this.showCloneExtra = false;
      this.selectedExtra = null;
      this.refetchSupplier();
    },
    extraMapper(item) {
      const { agentGroup, service, ...rest } = item.node;
      if (service === null) {
        //http://tracker.globetrack.ie/issues/5855
        return { ...rest, serviceId: null, serviceName: "" };
      }
      return { ...rest, serviceId: service.id, serviceName: service.name };
    },
    onCloseAddExtras() {
      this.supplierAddExtrasDialog = false;
    },
    refetchSupplier() {
      if (this.$apollo.queries.supplier != null) {
        this.$apollo.queries.supplier.refetch();
      }
    },
    closeAndRefetch(close = true) {
      if (close) {
        this.supplierAddExtrasDialog = false;
        this.supplierExtraEditorDialog = false;
      }

      this.refetchSupplier();
    },
    getGroupingMethod(groupByField) {
      return (list) => {
        return list.reduce((group, item) => {
          const key = item[groupByField];
          if (!group[key]) {
            group[key] = { label: key, items: [] };
          }
          group[key].items.push(item);
          return group;
        }, {});
      };
    },
    onEditItem(item) {
      this.editedItem = item;
      this.supplierExtraEditorDialog = true;
    },
    onUpdateItem(item) {
      console.log("updateItem", item);
      //clean up the item
      const {
        id,
        agentGroupName,
        typeId,
        __typename,
        serviceId,
        serviceName,
        ...input
      } = item;
      this.$apollo
        .mutate({
          mutation: EXTRA_UPDATE_MUTATION,
          variables: {
            id: id,
            extraInput: input,
          },
        })
        .then((data) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Extra updated",
          });
          this.refetchSupplier();
        });
    },
    onDeleteItem(item) {
      this.$apollo
        .mutate({
          mutation: EXTRA_DELETE_MUTATION,
          variables: {
            id: item.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", {
        msg: "Extra Removed.",
      });
      //https://vue-apollo.netlify.com/guide/components/mutation.html#updating-the-cache
      //https://vue-apollo.netlify.com/guide/apollo/mutations.html
      // update cache instead of refetching
      this.refetchSupplier();
    },
    removeGqlObjectErrorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
    },
  },
  apollo: {
    supplier: {
      query: QUERY,
      variables() {
        return {
          supplierId: this.supplierId,
          extraName_contains: this.extraNameFilter,
          extraValidTo_gte: this.dateFrom,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          extraServices: this.selectedServices,
          serviceNotInUse: this.serviceNotInUseFilter,
        };
      },
      fetchPolicy: "network-only",
      skip() {
        return !this.supplierId;
      },
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/suppliers/extras/SupplierExtras.vue